import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Activity, Server, Database, Webhook, AlertTriangle, Mail, RefreshCw, CheckCircle2, XCircle, Clock, Bell } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return "?"; }
}
function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function HealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data } = await API.get("/admin/health");
      setData(data);
    } catch { /* toast handled in interceptor */ }
    finally { setLoading(false); setRefreshing(false); }
  };

  const sendTestAlert = async () => {
    try {
      const { data } = await API.post("/admin/health/test-alert");
      if (data.ok) toast.success(`Test alert sent to ${data.label} <${data.to}>`);
      else toast.error("Test alert failed");
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test alert failed");
    }
  };
  useEffect(() => { load(); const t = setInterval(() => load(true), 15000); return () => clearInterval(t); }, []);

  if (loading) return <div className="p-10 text-center text-slate-400 dark:text-slate-500">Loading system health…</div>;
  if (!data) return <div className="p-10 text-center text-rose-600">Failed to load health data</div>;

  const healthy = data.overall === "healthy";

  return (
    <div className="space-y-6" data-testid="health-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <Activity className="w-6 h-6 text-emerald-600" /> System Health
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 mt-1">Live operations view — worker, cache, Mongo, webhooks & error pipeline.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 dark:text-slate-500">Checked {timeAgo(data.checked_at)} · auto-refresh 15s</span>
          <Button variant="outline" size="sm" onClick={sendTestAlert} data-testid="send-test-alert-btn"><Bell className="w-4 h-4 mr-1" /> Test Alert</Button>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={refreshing} data-testid="health-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh</Button>
        </div>
      </div>

      {/* Overall banner */}
      <Card className={healthy ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40"} data-testid="overall-banner">
        <CardContent className="p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${healthy ? "bg-emerald-100" : "bg-rose-100"}`}>
            {healthy ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-rose-600" />}
          </div>
          <div className="flex-1">
            <p className={`font-semibold ${healthy ? "text-emerald-800" : "text-rose-800"}`}>{healthy ? "All systems operational" : "Degraded service"}</p>
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{healthy ? "Worker heartbeat fresh · cache active · webhooks flowing" : "Check worker & error pipeline below"}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Worker card */}
        <Card data-testid="worker-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4 text-purple-600" /> Cron Worker</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Status</span>
              {data.worker.healthy
                ? <Badge className="bg-emerald-100 text-emerald-700">Healthy</Badge>
                : <Badge className="bg-rose-100 text-rose-700">No heartbeat</Badge>}
            </div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Last heartbeat</span>
              <span className="font-mono text-slate-700 dark:text-slate-200">{data.worker.last_heartbeat ? timeAgo(data.worker.last_heartbeat) : "—"}</span>
            </div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Jobs registered</span><span className="text-slate-900 dark:text-white font-semibold">{data.worker.jobs_registered}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">PID</span><span className="font-mono text-slate-400 dark:text-slate-500 text-xs">{data.worker.pid || "—"}</span></div>
          </CardContent>
        </Card>

        {/* Cache card */}
        <Card data-testid="cache-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4 text-amber-600" /> In-Process Cache</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Entries</span><span className="font-semibold text-slate-900 dark:text-white">{data.cache.entries}</span></div>
            <div className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 pt-1 border-t">Prefixes:</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.cache.by_prefix || {}).map(([p, c]) => (
                <span key={p} className="text-xs bg-slate-100 rounded-full px-2.5 py-1 font-mono">{p} × {c}</span>
              ))}
              {Object.keys(data.cache.by_prefix || {}).length === 0 && <span className="text-xs text-slate-400 dark:text-slate-500 italic">(cold)</span>}
            </div>
          </CardContent>
        </Card>

        {/* Pharmacy Webhooks card — LabFlo + LifeFile combined */}
        <Card data-testid="webhook-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Webhook className="w-4 h-4 text-indigo-600" /> Pharmacy Webhooks</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Total received</span><span className="font-semibold text-slate-900 dark:text-white">{data.webhooks.combined_total ?? data.webhooks.total}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Replayed</span><span className="font-semibold text-amber-600">{data.webhooks.replayed}</span></div>
            <div className="pt-1 border-t dark:border-slate-800">
              <div className="flex justify-between text-xs mt-1">
                <span className="text-slate-500 dark:text-slate-400">LabFlo</span>
                <span className="text-slate-700 dark:text-slate-200">
                  {data.webhooks.by_source?.labflo?.total ?? data.webhooks.total} events
                  {data.webhooks.by_source?.labflo?.last_received && (
                    <> · {timeAgo(data.webhooks.by_source.labflo.last_received.received_at)}</>
                  )}
                </span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-slate-500 dark:text-slate-400">Polomar · LifeFile</span>
                <span className="text-slate-700 dark:text-slate-200">
                  {data.webhooks.by_source?.lifefile?.total ?? 0} events
                  {data.webhooks.by_source?.lifefile?.last_received?.received_at && (
                    <> · {timeAgo(data.webhooks.by_source.lifefile.last_received.received_at)}</>
                  )}
                </span>
              </div>
            </div>
            <div className="flex justify-between pt-1 border-t dark:border-slate-800">
              <span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Last received</span>
              <span className="text-slate-700 dark:text-slate-200 text-xs text-right">
                {data.webhooks.last_received ? <>{timeAgo(data.webhooks.last_received.received_at)}<br/><span className="font-mono text-slate-400 dark:text-slate-500">{data.webhooks.last_received.new_status} · {data.webhooks.last_received.external_reference}</span></> : "never"}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Errors card */}
        <Card data-testid="errors-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-600" /> Error Pipeline</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Active</span><span className="font-semibold text-rose-600">{data.errors.active}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Acknowledged</span><span className="font-semibold text-amber-600">{data.errors.acknowledged}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Resolved</span><span className="font-semibold text-emerald-600">{data.errors.resolved}</span></div>
            <div className="flex justify-between pt-1 border-t"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Auto-send</span>
              {data.errors.auto_send_enabled ? <Badge className="bg-emerald-100 text-emerald-700">On</Badge> : <Badge className="bg-slate-100 text-slate-600 dark:text-slate-300">Off</Badge>}
            </div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Daily digest</span>
              {data.errors.digest_enabled ? <Badge className="bg-emerald-100 text-emerald-700">On</Badge> : <Badge className="bg-slate-100 text-slate-600 dark:text-slate-300">Off</Badge>}
            </div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Rate-limited IPs tracked</span><span className="font-mono text-slate-500 dark:text-slate-400 dark:text-slate-500">{data.errors.rate_limiter_ips_tracked}</span></div>
            <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 dark:text-slate-500">Routing</span><span className="text-slate-700 dark:text-slate-200 text-xs truncate max-w-[180px]">{data.errors.contact_label} &lt;{data.errors.contact_email}&gt;</span></div>
          </CardContent>
        </Card>
      </div>

      {/* Mongo table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Database className="w-4 h-4 text-emerald-600" /> MongoDB Collections</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm" data-testid="mongo-table">
            <thead className="border-b">
              <tr className="text-slate-500 dark:text-slate-400 dark:text-slate-500 text-xs uppercase tracking-wide">
                <th className="text-left py-2 font-medium">Collection</th>
                <th className="text-right py-2 font-medium">Docs</th>
                <th className="text-right py-2 font-medium">Data</th>
                <th className="text-right py-2 font-medium">Storage</th>
                <th className="text-right py-2 font-medium">Index</th>
                <th className="text-right py-2 font-medium">#idx</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
              {data.mongo.collections.map((c) => (
                <tr key={c.name} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-slate-800/40">
                  <td className="py-2 font-mono text-slate-900 dark:text-white text-xs">{c.name}</td>
                  <td className="text-right font-semibold text-slate-900 dark:text-white">{c.count.toLocaleString()}</td>
                  <td className="text-right text-slate-500 dark:text-slate-400 dark:text-slate-500">{fmtBytes(c.size_bytes)}</td>
                  <td className="text-right text-slate-500 dark:text-slate-400 dark:text-slate-500">{fmtBytes(c.storage_bytes)}</td>
                  <td className="text-right text-slate-500 dark:text-slate-400 dark:text-slate-500">{fmtBytes(c.index_bytes)}</td>
                  <td className="text-right text-slate-500 dark:text-slate-400 dark:text-slate-500">{c.num_indexes}</td>
                </tr>
              ))}
              <tr className="font-semibold bg-slate-50 dark:bg-slate-800/40">
                <td className="py-2 text-slate-700 dark:text-slate-200">Total</td>
                <td className="text-right text-slate-900 dark:text-white">{data.mongo.total_docs.toLocaleString()}</td>
                <td colSpan="1" className="text-right text-slate-500 dark:text-slate-400 dark:text-slate-500">—</td>
                <td className="text-right text-slate-700 dark:text-slate-200">{fmtBytes(data.mongo.total_storage_bytes)}</td>
                <td className="text-right text-slate-700 dark:text-slate-200">{fmtBytes(data.mongo.total_index_bytes)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Recent digests */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4 text-rose-600" /> Recent Error Digests</CardTitle></CardHeader>
        <CardContent>
          {data.digests_recent.length === 0 ? (
            <p className="text-slate-400 dark:text-slate-500 text-sm italic py-4 text-center" data-testid="digests-empty"><Clock className="w-3.5 h-3.5 inline mr-1" />No digests sent yet.</p>
          ) : (
            <div className="space-y-2" data-testid="digests-list">
              {data.digests_recent.map((d) => (
                <div key={d.sent_at || d.id || `${d.triggered_by}-${d.active_count}`} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    {d.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-rose-600" />}
                    <span className="text-slate-700 dark:text-slate-200">{timeAgo(d.sent_at)}</span>
                    <span className="text-slate-500 dark:text-slate-400 dark:text-slate-500 text-xs">· {d.active_count} active</span>
                  </div>
                  <span className="text-xs text-slate-400 dark:text-slate-500 italic">{d.triggered_by}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      {/* Health sentinel alerts */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Bell className="w-4 h-4 text-amber-600" /> Health Sentinel Alerts <span className="text-xs font-normal text-slate-400 dark:text-slate-500 ml-2">email fallback · throttled 1/hr/direction</span></CardTitle></CardHeader>
        <CardContent>
          {(!data.health_alerts_recent || data.health_alerts_recent.length === 0) ? (
            <p className="text-slate-400 dark:text-slate-500 text-sm italic py-4 text-center" data-testid="alerts-empty"><Clock className="w-3.5 h-3.5 inline mr-1" />No transition alerts sent yet — system stable since launch.</p>
          ) : (
            <div className="space-y-2" data-testid="alerts-list">
              {data.health_alerts_recent.map((a) => {
                const isDown = a.direction === "healthy_to_degraded";
                return (
                  <div key={a.sent_at || `${a.direction}-${a.id || ''}`} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      {isDown ? <XCircle className="w-4 h-4 text-rose-600" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                      <span className="text-slate-700 dark:text-slate-200">{timeAgo(a.sent_at)}</span>
                      <span className={`text-xs font-medium ${isDown ? "text-rose-700" : "text-emerald-700"}`}>
                        {isDown ? "System Degraded" : "System Recovered"}
                      </span>
                    </div>
                    <span className="text-xs text-slate-400 dark:text-slate-500 italic">→ {a.to}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
